/** A site's start screen: shortcuts to common tasks, publish state, recent edits. Plus the Global settings index. */
import { useEffect, useState } from 'react';
import { resolveItem, type SiteConfig } from '@hotpot/core';
import { Layout } from '../components/Layout.js';
import { ErrorBox, Loading, navItem, paths, Status } from '../components/ui.js';
import { useT } from '../i18n.js';
import { relativeTime } from '../lib/format.js';
import { Link } from '../router.js';
import { useAsync, useSite } from '../state.js';
import type { PublishStatus } from '@hotpot/core';

/** Dashboard link for a changed file, if it is content we know. */
export function routeForPath(config: SiteConfig, siteId: string, path: string): string | null {
  for (const c of config.collections) {
    if (c.type === 'folder') {
      const folder = c.folder.replace('{{locale}}', '[^/]+');
      const m = path.match(new RegExp(`^${folder.replace(/[.]/g, '\\.')}/([^/]+)\\.${c.extension}$`));
      if (m) return paths.entry(siteId, c.name, m[1]!);
    } else {
      for (const f of c.files) {
        const re = new RegExp(`^${f.file.replace(/[.]/g, '\\.').replace('{{locale}}', '[^/]+')}$`);
        if (re.test(path)) return paths.file(siteId, c.name, f.name);
      }
    }
  }
  return null;
}

export function SiteHome() {
  const { t, lang } = useT();
  const site = useSite();
  const id = site.handle.site.id;
  const recent = useAsync(() => site.content.history(undefined, 8), [site.version]);
  const [lastStatus, setLastStatus] = useState<PublishStatus | null>(null);
  useEffect(() => {
    const sha = recent.data?.[0]?.sha;
    if (!sha || site.publish) return;
    site.handle.storage.publishStatus(sha).then(setLastStatus, () => setLastStatus(null));
  }, [recent.data, site.publish, site.handle.storage]);

  const shortcuts = site.config.start?.shortcuts ?? [];
  // With drafts, the newest commit can be a draft (its build is the preview), so only trust it when nothing is pending.
  const status = site.publish?.status ?? (site.handle.drafts && site.pending?.files.length !== 0 ? null : lastStatus);

  return (
    <Layout>
      <div className="title-row">
        <div>
          <h1>{site.handle.site.name}</h1>
          <p className="desc">
            {site.handle.site.url && (
              <a href={site.handle.site.url} target="_blank" rel="noopener noreferrer">
                {site.handle.site.url.replace(/^https?:\/\//, '')} ↗
              </a>
            )}
          </p>
          {site.config.start?.intro && <p className="desc muted">{site.config.start.intro}</p>}
        </div>
        <div className="actions">{status && <Status status={status} url={site.handle.site.url || undefined} />}</div>
      </div>

      {shortcuts.length > 0 && (
        <>
          <h2 className="section-h">{t('quickActions')}</h2>
          <div className="tiles" style={{ marginTop: 14 }}>
            {shortcuts.map((s, i) => {
              let to = '';
              if (s.action === 'media') to = paths.media(id);
              else if (s.target) {
                const r = resolveItem(site.config, s.target);
                if (!r) return null;
                if (s.action === 'new' && r.collection.type === 'folder') to = paths.newEntry(id, r.collection.name);
                else if (r.file) to = paths.file(id, r.collection.name, r.file.name);
                else to = paths.list(id, r.collection.name);
              }
              return (
                <Link key={i} className="tile-btn" to={to}>
                  <span className="plus" aria-hidden="true">
                    {s.action === 'new' ? '+' : s.action === 'media' ? '↑' : '→'}
                  </span>
                  <span>{s.label}</span>
                </Link>
              );
            })}
          </div>
        </>
      )}

      <h2 className="section-h">{t('recentEdits')}</h2>
      {recent.error ? (
        <ErrorBox error={recent.error} onRetry={recent.reload} />
      ) : recent.loading && !recent.data ? (
        <Loading />
      ) : !recent.data?.length ? (
        <p className="muted">{t('noRecent')}</p>
      ) : (
        <ul className="history" style={{ borderTop: 0 }}>
          {recent.data.map((c) => {
            const to = c.files?.length === 1 ? routeForPath(site.config, id, c.files[0]!) : null;
            return (
              <li key={c.sha}>
                <span className="msg">{to ? <Link to={to}>{c.message}</Link> : c.message}</span>
                <span className="by">
                  {c.author.name} · {relativeTime(c.date, t, lang)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <p style={{ marginTop: 14 }}>
        <Link className="link-btn" to={paths.history(id)}>
          {t('history')} →
        </Link>
      </p>
    </Layout>
  );
}

export function SettingsIndex() {
  const { t } = useT();
  const site = useSite();
  const id = site.handle.site.id;
  return (
    <Layout>
      <div className="title-row">
        <h1>{t('globalSettings')}</h1>
      </div>
      <ul className="rows">
        {(site.config.settings ?? []).map((item) => {
          const n = navItem(site.config, id, item);
          const r = resolveItem(site.config, item);
          if (!n) return null;
          return (
            <li className="row" key={item}>
              <Link className="main" to={n.to}>
                <span className="t">{n.label}</span>
                {r?.file?.description && <span className="s">{r.file.description}</span>}
              </Link>
            </li>
          );
        })}
      </ul>
    </Layout>
  );
}
